import { API_BASE } from '../config';
import { translations, jezykStartowy } from '../translations';
import { zapiszPrzedWyjsciem, oznaczWylogowanie, trwaWylogowanie } from './zapisPrzedWyjsciem';

// Jedno wylogowanie dla całego panelu (wcześniej dwie kopie: App.js i sidebar.js, różniące się
// tym, czy czyszczą `isDemo`). Serwer kasuje sesję i ciasteczka, a pełne przeładowanie na
// /login gwarantuje, że przeglądarka przetworzy wyczyszczone ciasteczka sid/csrf.
// Imię, nazwisko i nazwa firmy też: zostawały w przeglądarce po wylogowaniu, więc na wspólnym
// komputerze następna osoba widziała je w localStorage (przegląd bezpieczeństwa 12.10.2026).
// Szkic oferty w sessionStorage zostaje celowo (klucz z numerem konta, znika z kartą).
const KLUCZE_SESJI = [
  'userId', 'isAdmin', 'isSuperAdmin', 'organizationId', 'isLoggedIn', 'csrfToken',
  'subscriptionPlan', 'isDemo', 'accountPaused', 'homeFavorites',
  'userFirstName', 'userLastName', 'organizationName',
];

// Po wylogowaniu strona nie pyta już o jej opuszczenie: niezapisane zmiany zapisały się chwilę
// wcześniej (zapiszPrzedWyjsciem), a jeśli się nie dało, bez sesji i tak nie da się ich zapisać.
// Konstruktory sprawdzają trwaWylogowanie() w swoim nasłuchu. Ten nasłuch w fazie przechwytywania
// zatrzymuje pytanie pozostałych stron (Dane firmy, Powiadomienia) i jest rejestrowany RAZ, przy
// wczytaniu modułu (App.js importuje go przed zamontowaniem stron): Chrome wywołuje nasłuchy
// zdarzenia wysłanego na window w kolejności rejestracji, więc nasłuch dopisany dopiero przy
// wylogowaniu biegł PO nasłuchu strony, który zdążył już zapytać (sprawdzenie 12.10.2026).
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (trwaWylogowanie()) e.stopImmediatePropagation();
  }, { capture: true });
}

// Tekst nakładki poza drzewem Reacta: język jak w TranslationProvider.
const t = (klucz) => {
  let jezyk = 'pl';
  try { jezyk = jezykStartowy(localStorage.getItem('language')); } catch (_) { /* tryb prywatny */ }
  return (translations[jezyk] && translations[jezyk][klucz]) || translations.pl[klucz] || klucz;
};

// Nakładka „Zapisuję zmiany i wylogowuję…” (przegląd bezpieczeństwa 12.10.2026): przez chwilę
// czekania na zapis strona wyglądała, jakby „Wyloguj” nie zadziałało, a pisanie w tym czasie
// i tak by przepadło. Pokazywana dopiero, gdy zapis trwa dłużej niż mgnienie oka, i zostaje do
// przeładowania strony. Ponad oknami i listami, pod komunikatami ulotnymi (błąd zapisu).
const OPOZNIENIE_NAKLADKI_MS = 150;
function pokazNakladke() {
  if (typeof document === 'undefined' || document.querySelector('[data-cy="logout-overlay"]')) return;
  const tlo = document.createElement('div');
  tlo.setAttribute('data-cy', 'logout-overlay');
  tlo.setAttribute('role', 'alert');
  tlo.setAttribute('aria-busy', 'true');
  tlo.tabIndex = -1;
  tlo.style.cssText = [
    'position: fixed', 'inset: 0', 'z-index: var(--z-confirm, 1250)', 'display: flex',
    'align-items: center', 'justify-content: center', 'padding: 16px',
    'background: rgba(15, 23, 42, 0.55)', 'cursor: progress', 'outline: none',
  ].join(';');
  const okno = document.createElement('div');
  okno.style.cssText = [
    'background: var(--card-bg, #fff)', 'color: var(--text, #111827)',
    'padding: var(--sp-5, 20px) var(--sp-6, 24px)', 'border-radius: var(--r-lg, 12px)',
    'box-shadow: var(--shadow-lg, 0 20px 60px rgba(0, 0, 0, 0.3))',
    'font-size: var(--fs-base, 15px)', 'font-weight: 600',
  ].join(';');
  okno.textContent = t('sidebar.logoutSaving');
  tlo.appendChild(okno);
  document.body.appendChild(tlo);
  // Fokus z pola edycji na nakładkę: tekst wpisany w trakcie czekania i tak by przepadł.
  try { tlo.focus(); } catch (_) { /* bez znaczenia */ }
}

let wylogowujeSie = false;

export async function wyloguj() {
  // Drugi klik „Wyloguj” w trakcie czekania na zapis nie zaczyna drugiego wylogowania.
  if (wylogowujeSie) return;
  wylogowujeSie = true;
  const csrf = localStorage.getItem('csrfToken') || '';
  let wyslane = false;
  // keepalive: żądanie przeżywa zamknięcie karty i przeładowanie strony.
  const wyslijWylogowanie = () => {
    wyslane = true;
    return fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    });
  };
  const wyczyscSesje = () => KLUCZE_SESJI.forEach((k) => localStorage.removeItem(k));
  // Karta zamknięta w trakcie czekania na zapis: dawniej wylogowanie nie szło wcale, a sesja
  // zostawała na serwerze (przegląd bezpieczeństwa 12.10.2026). Teraz idzie przy zamknięciu.
  const przyZamknieciu = () => {
    if (wyslane) return;
    try {
      wyslijWylogowanie().catch(() => {});
    } catch (_) { /* przeglądarka bez keepalive */ }
    wyczyscSesje();
  };
  window.addEventListener('pagehide', przyZamknieciu);
  let zegarNakladki = null;
  try {
    // Najpierw zapis niezapisanych zmian (konstruktory), póki sesja jeszcze istnieje.
    await zapiszPrzedWyjsciem({
      przyZapisie: () => { zegarNakladki = setTimeout(pokazNakladke, OPOZNIENIE_NAKLADKI_MS); },
    });
  } catch (error) {
    console.error('Save before logout error:', error);
  } finally {
    clearTimeout(zegarNakladki);
  }
  try {
    if (!wyslane) await wyslijWylogowanie();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    window.removeEventListener('pagehide', przyZamknieciu);
    wyczyscSesje();
    oznaczWylogowanie();
    wylogowujeSie = false;
    window.location.replace('/login');
  }
}
